import { useRole } from '@/hooks/useRole'
import { Order, SharedProps } from '@/types'
import { formatStatus, getCustomerInfo, getStatusColor } from '@/utils/format'
import { Head, router } from '@inertiajs/react'
import { Badge, Button, Checkbox, Group, Paper, Stack, Table, Text, TextInput, Title } from '@mantine/core'
import { IconDeviceMobileMessage, IconSearch } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'

interface Props extends SharedProps {
  orders: Order[]
}

export default function OrderNotificationsIndex({ orders, current_organization }: Props) {
  const { isBoutique } = useRole()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>(orders.map((o) => o.id))

  const filteredOrders = useMemo(() => {
    if (!searchQuery.trim()) return orders

    const normalizedQuery = searchQuery.toLowerCase().trim()

    return orders.filter((order) => {
      const { name, phone } = getCustomerInfo(order)
      const orderId = order.id.toString()

      return (
        name.toLowerCase().includes(normalizedQuery) ||
        phone.toLowerCase().includes(normalizedQuery) ||
        orderId.includes(normalizedQuery)
      )
    })
  }, [orders, searchQuery])

  const toggleAll = () => {
    if (selectedOrderIds.length === filteredOrders.length && filteredOrders.length > 0) {
      setSelectedOrderIds([])
    } else {
      setSelectedOrderIds(filteredOrders.map((o) => o.id))
    }
  }

  const toggleOne = (id: number) => {
    setSelectedOrderIds((current) => (current.includes(id) ? current.filter((i) => i !== id) : [...current, id]))
  }

  const handleSendSms = () => {
    if (selectedOrderIds.length === 0) return

    router.post(`/organizations/${current_organization?.slug}/notifications`, {
      order_ids: selectedOrderIds,
    })
  }

  return (
    <>
      <Head title={`Envoi SMS - ${current_organization?.name}`} />

      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title>Envoi SMS</Title>
          <Button
            leftSection={<IconDeviceMobileMessage size={18} />}
            disabled={selectedOrderIds.length === 0}
            onClick={handleSendSms}
            color="green"
          >
            Envoyer SMS ({selectedOrderIds.length})
          </Button>
        </Group>

        <TextInput
          placeholder="Rechercher par client, numéro ou téléphone..."
          leftSection={<IconSearch size={16} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <Paper withBorder radius="md" p={{ base: 0, sm: 'md' }}>
          <Table.ScrollContainer minWidth={600}>
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 40 }}>
                    <Checkbox
                      checked={selectedOrderIds.length === filteredOrders.length && filteredOrders.length > 0}
                      indeterminate={selectedOrderIds.length > 0 && selectedOrderIds.length < filteredOrders.length}
                      onChange={toggleAll}
                    />
                  </Table.Th>
                  <Table.Th style={{ width: 80 }}>N°</Table.Th>
                  <Table.Th>Client</Table.Th>
                  <Table.Th>Téléphone</Table.Th>
                  <Table.Th style={{ width: 100 }}>Prix</Table.Th>
                  <Table.Th visibleFrom="sm" style={{ width: 150 }}>
                    Prêt depuis
                  </Table.Th>
                  <Table.Th style={{ width: 120 }}>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredOrders.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={7} style={{ textAlign: 'center', padding: '3rem' }}>
                      <Text c="dimmed">Aucune commande prête pour l'envoi de SMS.</Text>
                    </Table.Td>
                  </Table.Tr>
                )}

                {filteredOrders.map((order) => {
                  const customerInfo = getCustomerInfo(order)
                  return (
                    <Table.Tr key={order.id} onClick={() => toggleOne(order.id)} style={{ cursor: 'pointer' }}>
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedOrderIds.includes(order.id)}
                          onChange={() => toggleOne(order.id)}
                        />
                      </Table.Td>
                      <Table.Td fw={500}>#{order.id}</Table.Td>
                      <Table.Td>{customerInfo.name}</Table.Td>
                      <Table.Td>
                        <Text size="sm">{customerInfo.phone}</Text>
                      </Table.Td>
                      <Table.Td fw={500}>{order.price}€</Table.Td>
                      <Table.Td visibleFrom="sm">
                        <Text c="dimmed" size="xs">
                          {dayjs(order.updated_at).format('DD/MM/YYYY HH:mm')}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={getStatusColor(order, isBoutique)} variant="light" size="sm">
                          {formatStatus(order, isBoutique)}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  )
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>
      </Stack>
    </>
  )
}
